import { toast } from 'react-toastify';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FaRobot } from 'react-icons/fa';
import { CreditAnalysis } from './types';
import type { AiAnalysisResultItem } from './types';
import ApprovalModal from './ApprovalModal';
import BatchApprovalModal from './BatchApprovalModal';
import UnifiedDraweeModal from './UnifiedDraweeModal';
import CcirModal from './CcirModal';
import PageHeader from '../../molecules/PageHeader';
import DataTable from './components/DataTable';
import ActionButton from './components/ActionButton';
import { useColumns } from './components/useColumns';
import { saveDecision, saveBatchDecision, getFeatureFlags, getBatchDetail, listAnalysisItems, requestAiAnalysis, reprocessAiAnalysis, reprocessAiAnalysisItem, ApiError, BatchDetail } from '../../../services/CreditAnalysisService';
import AiAnalysisModal from './AiAnalysisModal';
import AiRationaleModal from './AiRationaleModal';
import useAiAnalysisPolling from './useAiAnalysisPolling';
import AnalysisColumns from './shared/analysisColumns';
import { exportAnalysisToExcel } from './shared/exportToExcel';
import { parseRangeFilter } from './shared/parseRangeFilter';
import { fmtCurrency } from './utils/currencyUtils';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS & VARIABLES
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

const parseVal = (v: string) => { const n = Number.parseFloat(String(v).replace(',', '.')); return isNaN(n) ? 0 : n; };

// A reprocess dispatch failure is only *known* to have dispatched nothing when the
// backend rejected it deterministically before enqueueing: 404 (not found) or 422
// (no bureau data). Network timeouts / 5xx are ambiguous — the async job may have
// been accepted after the failure — so we must reconcile with the batch status
// instead of assuming nothing ran.
const isDefinitelyNotDispatched = (err: unknown): boolean =>
    err instanceof ApiError && (err.status === 404 || err.status === 422);
const fmtPct = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

const calcPdefPond = (items: CreditAnalysis[]) => {
    const pjItems = items.filter(r => r.type === 'PJ');
    const sumW = pjItems.reduce((s, r) => s + parseVal(r.approvedValue) * parseVal(r.pdef), 0);
    const sumA = pjItems.reduce((s, r) => s + parseVal(r.approvedValue), 0);
    return sumA > 0 ? sumW / sumA : 0;
};

const calcPdefAgroPond = (items: CreditAnalysis[]) => {
    const sumW = items.reduce((s, r) => s + parseVal(r.approvedValue) * parseVal(r.pdefAgro), 0);
    const sumA = items.reduce((s, r) => s + parseVal(r.approvedValue), 0);
    return sumA > 0 ? sumW / sumA : 0;
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function BatchAnalysisView() {
    const { batchId } = useParams<{ batchId: string }>();

    const [batch, setBatch] = useState<BatchDetail | null>(null);
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [sortField, setSortField] = useState('date');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [showFilters, setShowFilters] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAnalysis, setSelectedAnalysis] = useState<CreditAnalysis | null>(null);
    const [analysisData, setAnalysisData] = useState<CreditAnalysis[]>([]);
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [isBatchApprovalModalOpen, setIsBatchApprovalModalOpen] = useState(false);
    const [draweeModalDoc, setDraweeModalDoc] = useState<string | null>(null);
    const [draweeModalItemId, setDraweeModalItemId] = useState<string | null>(null);
    const [draweeModalName, setDraweeModalName] = useState<string | null>(null);
    const [ccirModalRow, setCcirModalRow] = useState<CreditAnalysis | null>(null);
    const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
    const [isAiModalOpen, setIsAiModalOpen] = useState(false);
    const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
    const [aiRationaleRow, setAiRationaleRow] = useState<CreditAnalysis | null>(null);
    const [aiRestartKey, setAiRestartKey] = useState(0);
    const [aiJustDispatched, setAiJustDispatched] = useState(false);
    // Optimistic "we just fired a reprocess POST" flag. Covers the short window
    // between the click and the backend reflecting PROCESSING. The authoritative
    // in-progress signal is aiData.status === 'PROCESSING' (reload-safe), combined
    // below into `reprocessing`.
    const [reprocessDispatching, setReprocessDispatching] = useState(false);
    const [reprocessingItemId, setReprocessingItemId] = useState<string | null>(null);

    useEffect(() => { getFeatureFlags().then(setFeatureFlags).catch(() => {}); }, []);

    // Experimental feature — opt-in only (hidden unless the flag is explicitly true)
    const aiAnalysisEnabled = featureFlags['credit-analysis:ai-analysis'] === true;

    const { data: aiData, loading: aiLoading, error: aiError, dataRestartKey: aiDataRestartKey } = useAiAnalysisPolling({ batchId: batchId || '', enabled: aiAnalysisEnabled && !!batchId, restartKey: aiRestartKey, keepPollingOnNotStarted: aiJustDispatched });

    // Clear the "just dispatched" flag only once the poll has produced a response
    // for the CURRENT restart (aiDataRestartKey === aiRestartKey) that already
    // left NOT_STARTED. Comparing against a stale terminal result (e.g. a previous
    // COMPLETED before a reprocess) would clear the flag too early and stop polling
    // before the new run's results land.
    useEffect(() => {
        if (aiJustDispatched && aiData && aiDataRestartKey === aiRestartKey && aiData.status !== 'NOT_STARTED') {
            setAiJustDispatched(false);
        }
    }, [aiData, aiJustDispatched, aiDataRestartKey, aiRestartKey]);

    // Authoritative "a reprocess is in progress" signal, per the AF-3610 integration
    // guide: the backend clears results after dispatch so the status query reports
    // PROCESSING (reload-safe). We OR that with the short-lived optimistic dispatch
    // flag to cover the window before the backend reflects PROCESSING. This is what
    // keeps the buttons disabled / labelled "Reprocessando..." until the run ends —
    // NOT the POST's resolution.
    const reprocessing = reprocessDispatching || aiData?.status === 'PROCESSING';

    // Clear the optimistic dispatch flag once the poll produces a current-restart
    // response that already LEFT NOT_STARTED (PROCESSING, COMPLETED or FAILED). We
    // must keep it during a transient NOT_STARTED — that is exactly the window
    // aiJustDispatched/keepPollingOnNotStarted covers, and dropping the guard there
    // would re-enable both buttons before the run starts, allowing a second
    // overwrite. Leaving NOT_STARTED also covers a run that finishes within one poll
    // interval (terminal on first response), so the buttons never get stranded.
    useEffect(() => {
        if (reprocessDispatching && aiData && aiDataRestartKey === aiRestartKey && aiData.status !== 'NOT_STARTED') {
            setReprocessDispatching(false);
        }
    }, [reprocessDispatching, aiData, aiDataRestartKey, aiRestartKey]);

    // When the run leaves PROCESSING, drop the per-item spinner marker.
    // Drop the per-item spinner marker only once the current run reaches a real
    // terminal status (COMPLETED/FAILED). A transient NOT_STARTED right after
    // dispatch is still "in progress" (keepPollingOnNotStarted covers it), so
    // clearing on NOT_STARTED would drop the spinner before the run actually starts.
    useEffect(() => {
        if (reprocessingItemId && aiData && aiDataRestartKey === aiRestartKey
            && aiData.status !== 'PROCESSING' && aiData.status !== 'NOT_STARTED') {
            setReprocessingItemId(null);
        }
    }, [reprocessingItemId, aiData, aiDataRestartKey, aiRestartKey]);

    useEffect(() => {
        if (!batchId) return;
        getBatchDetail(batchId).then(setBatch).catch(err => console.error('Failed to load batch:', err));
    }, [batchId]);

    const loadAnalysisItems = useCallback(() => {
        if (!batchId) return;
        listAnalysisItems({ batchId, size: 9999 })
            .then(data => { setAnalysisData((data.content || []) as CreditAnalysis[]); })
            .catch(() => { toast.error('Erro ao carregar dados'); setAnalysisData([]); });
    }, [batchId]);

    useEffect(() => { loadAnalysisItems(); }, [loadAnalysisItems]);

    // Merge AI results into the rows reactively (pure derivation, no race condition)
    const mergedData = useMemo(() => {
        if (!aiData || aiData.results.length === 0) return analysisData;
        const byItem = new Map(aiData.results.map(r => [r.analysisItemId, r]));
        return analysisData.map(row => {
            const ai = byItem.get(row.uuid);
            if (!ai) return row;
            return {
                ...row,
                aiRecommendation: ai.recommendation,
                aiConfidence: ai.confidence,
                aiPdefault1y: ai.pdefault1y,
                aiRationale: ai.rationale,
                aiRiskFactors: ai.riskFactors,
                aiPositiveFactors: ai.positiveFactors,
                aiErrorMessage: ai.errorMessage,
            };
        });
    }, [analysisData, aiData]);

    const { filteredData } = useMemo(() => {
        let filtered = [...mergedData];
        const multiselectFields = new Set(['rating', 'recommendation', 'status', 'type']);
        const rangeFields = new Set(['originalValue', 'approvedValue', 'suggestedValue', 'currentExposure', 'potentialExposure', 'scrTotal', 'scrRural', 'overdue', 'scrPrejuizo', 'qsa', 'annualRevenue', 'equity', 'score', 'agroScore', 'pdef', 'pdefAgro', 'scoreQuod', 'scoreBoaVista', 'ccirHa', 'ccirValue', 'patrimonioScr', 'ccirParticipation', 'ccirPatrimSacado', 'ccirPatrimDividaGrupo', 'aiPdefault1y', 'aiConfidence', 'dupPercent', 'approvedPercent']);
        // Fields displayed in R$mil (divided by 1000) — filter input should match displayed value
        const displayedInThousands = new Set(['scrTotal', 'scrRural', 'overdue', 'scrPrejuizo', 'qsa', 'ccirPatrimSacado']);
        // Fields stored as 0.0–1.0 fractions but displayed as percentages — filter must match displayed value
        const displayedAsPercent = new Set(['aiPdefault1y', 'aiConfidence', 'dupPercent', 'approvedPercent']);

        Object.entries(filters).forEach(([field, value]) => {
            if (!value) return;
            if (rangeFields.has(field)) {
                const predicate = parseRangeFilter(value);
                if (!predicate) return;
                filtered = filtered.filter(item => {
                    const raw = (item as any)[field];
                    let num = Number.parseFloat(String(raw ?? '').replace(/[^\d.,-]/g, '').replace(',', '.'));
                    if (isNaN(num)) return false;
                    // Convert to display units so filter matches what user sees
                    if (displayedInThousands.has(field)) num = num / 1000;
                    if (displayedAsPercent.has(field)) num = num * 100;
                    return predicate(num);
                });
                return;
            }
            const vals = value.split(';').map(v => v.trim().toLowerCase()).filter(Boolean);
            const isMultiselect = multiselectFields.has(field);
            filtered = filtered.filter(item => {
                const iv = (item as any)[field];
                if (!iv) return isMultiselect && vals.includes('-');
                const low = String(iv).toLowerCase();
                return isMultiselect ? vals.some(v => v === low) : vals.some(v => low.includes(v));
            });
        });

        if (sortField) {
            filtered.sort((a, b) => {
                const cmp = String((a as any)[sortField] || '').localeCompare(String((b as any)[sortField] || ''), 'pt-BR', { numeric: true });
                return sortOrder === 'ASC' ? cmp : -cmp;
            });
        }

        return { filteredData: filtered };
    }, [filters, sortField, sortOrder, mergedData]);

    const handleSort = (field: string) => {
        if (sortField === field) setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
        else { setSortField(field); setSortOrder('ASC'); }
    };

    const handleSelectRow = (uuid: string) => {
        setSelectedRows(prev => { const s = new Set(prev); s.has(uuid) ? s.delete(uuid) : s.add(uuid); return s; });
    };

    const handleSelectAll = () => {
        setSelectedRows(prev => prev.size === filteredData.length ? new Set() : new Set(filteredData.map(r => r.uuid)));
    };

    const statusToApi: Record<string, string> = {
        'Pendente': 'PENDING', 'Aprovado': 'APPROVED', 'Reprovado': 'REJECTED', 'Pré-aprovado': 'PRE_APPROVED', 'Cancelado': 'CANCELED',
    };

    const handleSaveBatchApproval = async (decision: string, justification: string, observation: string) => {
        const selected = analysisData.filter(i => selectedRows.has(i.uuid));
        if (!selected.length) return;
        try {
            const apiStatus = statusToApi[decision] || decision;
            const isApproval = ['APPROVED', 'PRE_APPROVED'].includes(apiStatus);
            const byBatch: Record<string, typeof selected> = {};
            selected.forEach(i => { (byBatch[i.id] = byBatch[i.id] || []).push(i); });
            for (const bid of Object.keys(byBatch)) {
                const items = byBatch[bid].map((i: CreditAnalysis) => ({ itemId: i.uuid, approvedValue: isApproval ? i.originalValue : undefined }));
                await saveBatchDecision(bid, items, { status: apiStatus, justification: justification || undefined, creditObs: observation || undefined });
            }
            setAnalysisData(prev => prev.map(i => {
                if (!selectedRows.has(i.uuid)) return i;
                return { ...i, status: apiStatus, approvedValue: isApproval ? i.originalValue : '', justification: justification || i.justification, creditObs: observation || i.creditObs };
            }));
            setSelectedRows(new Set());
        } catch { toast.error('Erro ao salvar decisão em lote'); }
    };

    const handleRequestAiAnalysis = async () => {
        if (!batchId) return;
        setAiAnalysisLoading(true);
        try {
            const { data } = await requestAiAnalysis(batchId);
            toast.info(data.message);
            setAiJustDispatched(true);
            setAiRestartKey(k => k + 1);
            setIsAiModalOpen(true);
        } catch (err) {
            // 409 = analysis already requested for this batch: just open the modal to show progress/results
            if (err instanceof ApiError && err.status === 409) {
                setAiRestartKey(k => k + 1);
                setIsAiModalOpen(true);
            } else {
                toast.error(err instanceof Error ? err.message : 'Erro ao solicitar análise de IA');
            }
        } finally {
            setAiAnalysisLoading(false);
        }
    };

    const handleReprocessBatch = async () => {
        // Shared guard: block while ANY reprocess is running (dispatching OR the
        // backend still reports PROCESSING), so two async jobs can't race to
        // overwrite the same batch.
        if (!batchId || reprocessing) return;
        setReprocessDispatching(true);
        try {
            const { data } = await reprocessAiAnalysis(batchId);
            toast.info(data.message);
            // Restart polling so the overwritten results are picked up as they land.
            // The optimistic flag is handed off to the PROCESSING status by the effect
            // above; we do NOT clear it here on success.
            setAiJustDispatched(true);
            setAiRestartKey(k => k + 1);
        } catch (err) {
            if (isDefinitelyNotDispatched(err)) {
                // 404/422: nothing was dispatched — clear the flag so the button re-enables.
                setReprocessDispatching(false);
                toast.error(err instanceof Error ? err.message : 'Erro ao reprocessar análise de IA');
                return;
            }
            // Ambiguous failure (network/5xx): the async job may have been accepted.
            // Restart polling and KEEP the guard — the batch status reconciles it
            // (PROCESSING keeps buttons locked; a preserved terminal status releases them).
            setAiJustDispatched(true);
            setAiRestartKey(k => k + 1);
            toast.warning('Falha de comunicação ao reprocessar. Verificando o estado do lote...');
        }
    };

    const handleReprocessItem = async (item: AiAnalysisResultItem) => {
        if (reprocessing) return;
        setReprocessDispatching(true);
        setReprocessingItemId(item.analysisItemId);
        try {
            const { data } = await reprocessAiAnalysisItem(item.analysisItemId);
            toast.info(data.message);
            setAiJustDispatched(true);
            setAiRestartKey(k => k + 1);
        } catch (err) {
            if (isDefinitelyNotDispatched(err)) {
                // 404/422: nothing dispatched — clear both flags so the row re-enables.
                setReprocessDispatching(false);
                setReprocessingItemId(null);
                toast.error(err instanceof Error ? err.message : 'Erro ao reprocessar o sacado');
                return;
            }
            // Ambiguous failure: the item may still have been dispatched. Restart
            // polling and keep the guard + spinner — the batch status reconciles it.
            setAiJustDispatched(true);
            setAiRestartKey(k => k + 1);
            toast.warning('Falha de comunicação ao reprocessar. Verificando o estado do sacado...');
        }
    };

    const columns = useColumns<CreditAnalysis>(() => AnalysisColumns({
        onOpenModal: (row) => { setSelectedAnalysis(row); setIsModalOpen(true); },
        onOpenDrawee: (doc, itemId, name, _batchId) => { setDraweeModalDoc(doc); setDraweeModalItemId(itemId); setDraweeModalName(name); },
        onOpenCcirModal: (row) => setCcirModalRow(row),
        onSaveDecision: saveDecision,
        onUpdateRow: (uuid, updates) => setAnalysisData(prev => prev.map(i => i.uuid === uuid ? { ...i, ...updates } as CreditAnalysis : i)),
        onOpenAiRationale: (row) => setAiRationaleRow(row),
        featureFlags,
        hideBatchColumn: true,
    }), [featureFlags]);

    // ── Footer totals ────────────────────────────────────────────────

    return (
        <div className="h-screen overflow-hidden flex flex-col bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100">
            {/* Batch header */}
            <div className="px-4 pt-4 pb-3 border-b border-gray-200 dark:border-slate-600">
                <PageHeader
                    title={batch?.batchName || 'Carregando...'}
                    apiVersion="Credit Analysis - 1.0.0"
                    breadcrumb={[
                        { label: 'Processamentos', url: '/credit-analysis/processamentos' },
                        { label: 'Detalhes do Lote', url: `/credit-analysis/batch/${batchId}` },
                        { label: 'Análise', url: '#' },
                    ]}
                >
                    <div className="flex items-center gap-3 pt-6">
                        {batch && (
                            <span className={`px-3 py-1 rounded text-xs font-medium ${
                                batch.status === 'COMPLETED' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                batch.status === 'ERROR' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            }`}>
                                {batch.status}
                            </span>
                        )}
                        <ActionButton
                            variant="secondary"
                            size="sm"
                            icon={<FaRobot />}
                            onClick={handleRequestAiAnalysis}
                            disabled={aiAnalysisLoading || !batch || batch.status !== 'COMPLETED'}
                            style={{ display: aiAnalysisEnabled ? undefined : 'none' }}
                        >
                            {aiAnalysisLoading ? 'Enviando...' : 'Análise IA'}
                        </ActionButton>
                    </div>
                </PageHeader>
                {batch && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Número: {batch.batchNumber} | Cedente: {batch.assignorName} | Criado em: {new Date(batch.createdAt).toLocaleString('pt-BR')}
                    </p>
                )}
            </div>

            {/* Table area */}
            <div className="flex-1 overflow-hidden flex flex-col px-4">
                {/* Action bar */}
                <div className="mt-4 mb-2 flex items-center justify-end gap-3 p-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Ações em Lote:</span>
                    <ActionButton variant="secondary" size="sm" onClick={() => setShowFilters(f => !f)}>
                        {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                    </ActionButton>
                    <ActionButton variant="warning" size="sm" onClick={() => setFilters({})}>
                        Limpar Filtros
                    </ActionButton>
                    <ActionButton variant="success" size="sm" onClick={() => exportAnalysisToExcel(filteredData, { includeAiColumns: aiAnalysisEnabled, hideBatchColumns: true })}>
                        Exportar Excel
                    </ActionButton>
                    <ActionButton
                        variant="success"
                        size="sm"
                        onClick={() => setIsBatchApprovalModalOpen(true)}
                        disabled={selectedRows.size === 0}
                        style={{ display: featureFlags['credit-analysis:batch-approve'] === false ? 'none' : undefined }}
                    >
                        Aprovação em Lote
                    </ActionButton>
                </div>

                <DataTable<CreditAnalysis>
                    columns={columns}
                    data={filteredData}
                    rowKey={(row) => row.uuid}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    showFilters={showFilters}
                    showCheckbox={true}
                    selectedRows={selectedRows}
                    onSelectRow={handleSelectRow}
                    onSelectAll={handleSelectAll}
                    emptyMessage="Nenhum registro encontrado..."
                />

                {/* Footer with totals */}
                <div className="sticky bottom-0 border-t border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 flex-shrink-0 z-10 px-4 py-3">
                    {(() => {
                        const allOriginal = analysisData.reduce((s, r) => s + parseVal(r.originalValue), 0);
                        const allApproved = analysisData.reduce((s, r) => s + parseVal(r.approvedValue), 0);
                        const filtOriginal = filteredData.reduce((s, r) => s + parseVal(r.originalValue), 0);
                        const filtApproved = filteredData.reduce((s, r) => s + parseVal(r.approvedValue), 0);
                        const selItems = filteredData.filter(r => selectedRows.has(r.uuid));
                        const selOriginal = selItems.reduce((s, r) => s + parseVal(r.originalValue), 0);
                        const selApproved = selItems.reduce((s, r) => s + parseVal(r.approvedValue), 0);
                        return (
                            <div className="flex gap-12 text-sm">
                                <div>
                                    <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Lote ({analysisData.length} itens)</div>
                                    <div>Total duplicatas: <span className="font-semibold">{fmtCurrency(allOriginal, false)}</span></div>
                                    <div>Total aprovado: <span className="font-semibold">{fmtCurrency(allApproved, false)}</span></div>
                                    <div>Diferença: <span className="font-semibold">{fmtCurrency(allOriginal - allApproved, false)}</span></div>
                                    <div>PDefault Positivo Pond.: <span className="font-semibold">{fmtPct(calcPdefPond(analysisData))}</span></div>
                                    <div>PDefault Agro Pond.: <span className="font-semibold">{fmtPct(calcPdefAgroPond(analysisData))}</span></div>
                                </div>
                                {filteredData.length < analysisData.length && (
                                    <div>
                                        <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Filtrado ({filteredData.length} itens)</div>
                                        <div>Total duplicatas: <span className="font-semibold">{fmtCurrency(filtOriginal, false)}</span></div>
                                        <div>Total aprovado: <span className="font-semibold">{fmtCurrency(filtApproved, false)}</span></div>
                                        <div>Diferença: <span className="font-semibold">{fmtCurrency(filtOriginal - filtApproved, false)}</span></div>
                                        <div>PDefault Positivo Pond.: <span className="font-semibold">{fmtPct(calcPdefPond(filteredData))}</span></div>
                                        <div>PDefault Agro Pond.: <span className="font-semibold">{fmtPct(calcPdefAgroPond(filteredData))}</span></div>
                                    </div>
                                )}
                                {selItems.length > 0 && (
                                    <div>
                                        <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Selecionado ({selItems.length} itens)</div>
                                        <div>Total duplicatas: <span className="font-semibold">{fmtCurrency(selOriginal, false)}</span></div>
                                        <div>Total aprovado: <span className="font-semibold">{fmtCurrency(selApproved, false)}</span></div>
                                        <div>Diferença: <span className="font-semibold">{fmtCurrency(selOriginal - selApproved, false)}</span></div>
                                        <div>PDefault Positivo Pond.: <span className="font-semibold">{fmtPct(calcPdefPond(selItems))}</span></div>
                                        <div>PDefault Agro Pond.: <span className="font-semibold">{fmtPct(calcPdefAgroPond(selItems))}</span></div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Modals */}
            {selectedAnalysis && <ApprovalModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} analysis={selectedAnalysis} />}
            {draweeModalDoc && (
                <UnifiedDraweeModal
                    batchId={batchId || ''}
                    itemId={draweeModalItemId || ''}
                    document={draweeModalDoc}
                    draweeName={draweeModalName || undefined}
                    onClose={() => { setDraweeModalDoc(null); setDraweeModalItemId(null); setDraweeModalName(null); }}
                />
            )}
            <BatchApprovalModal isOpen={isBatchApprovalModalOpen} onClose={() => setIsBatchApprovalModalOpen(false)} selectedCount={selectedRows.size} onSave={handleSaveBatchApproval} />
            {ccirModalRow && (
                <CcirModal
                    isOpen={!!ccirModalRow}
                    onClose={() => setCcirModalRow(null)}
                    row={ccirModalRow}
                    onDeepAnalysisComplete={() => {
                        // Backend recomputed CCIR figures; reload rows so the table
                        // reflects the API values (single source of truth).
                        loadAnalysisItems();
                    }}
                />
            )}
            {batchId && (
                <AiAnalysisModal
                    isOpen={isAiModalOpen}
                    onClose={() => setIsAiModalOpen(false)}
                    batchId={batchId}
                    justDispatched={aiJustDispatched}
                    data={aiData}
                    loading={aiLoading}
                    error={aiError}
                    onReprocessBatch={handleReprocessBatch}
                    onReprocessItem={handleReprocessItem}
                    reprocessInFlight={reprocessing}
                    reprocessingItemId={reprocessingItemId}
                />
            )}
            <AiRationaleModal row={aiRationaleRow} onClose={() => setAiRationaleRow(null)} />
        </div>
    );
}
